/**
 * Persistent on-device media cache for messages, photos, videos, and audio notes.
 *
 * Uses IndexedDB ('bazzef-media-cache') for persistent on-device binary storage,
 * with an in-memory LRU pool of Object URLs (blob:...) for zero-latency synchronous rendering.
 *
 * Capabilities:
 * - Persistent storage on the user's device across app reloads, offline sessions, and restarts.
 * - Instant synchronous paint for previously viewed/prefetched media (0ms delay, no white flashes).
 * - Background preloading queue for active chat threads (pre-fetches incoming media so it's already on device).
 * - Automatic LRU quota management (evicts least recently accessed media when approaching size limits).
 * - Explicit "Save to Device / Camera Roll" function for photos and videos.
 */

import { getMediaUrl } from './mediaUrl';

const DB_NAME = 'bazzef-media-cache';
const DB_VERSION = 1;
const STORE_NAME = 'media_blobs';
const MAX_ACTIVE_MEMORY_URLS = 64;
const MAX_CACHE_ENTRIES = 500;
const MAX_CACHE_BYTES = 300 * 1024 * 1024; // 300 MB

// In-memory active blob: URL pool for instant synchronous access
// Map<string, { blobUrl: string, blob: Blob, mimeType: string, size: number, lastUsed: number }>
const activeBlobUrls = new Map();

// Deduplication queue for in-flight downloads: Map<string, Promise<{ blob: Blob, blobUrl: string }>>
const inFlightRequests = new Map();

// Background preload queue
const preloadQueue = [];
let isPreloading = false;
let activePreloadCount = 0;
const MAX_CONCURRENT_PRELOAD = 2;

/**
 * Normalizes media URLs into a stable storage key.
 * Strips query strings, hashes, and protocol/host to treat relative and absolute URLs identically.
 *
 * @param {string} url
 * @returns {string} Normalized storage key
 */
export function normalizeMediaKey(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return '';

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const parsed = new URL(trimmed);
      return parsed.pathname.replace(/^\/+/, '');
    }
  } catch {
    // fallback to regex strip
  }

  return trimmed.split('?')[0].split('#')[0].replace(/^\/+/, '');
}

/**
 * Checks if a given media URL is cacheable (not an inline data/blob URL or empty string).
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isCacheableUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return Boolean(
    trimmed &&
    !trimmed.startsWith('data:') &&
    !trimmed.startsWith('blob:')
  );
}

/**
 * Opens or initializes the IndexedDB database.
 * @returns {Promise<IDBDatabase>}
 */
const openDb = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is unavailable on this device'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('by_accessed', 'lastAccessedAt', { unique: false });
        store.createIndex('by_size', 'size', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

/**
 * Revokes and drops oldest active memory blob URLs to keep RAM footprint minimal.
 */
function evictOldestMemoryUrls() {
  while (activeBlobUrls.size > MAX_ACTIVE_MEMORY_URLS) {
    let oldestKey = null;
    let oldestTime = Infinity;

    for (const [key, entry] of activeBlobUrls.entries()) {
      if (entry.lastUsed < oldestTime) {
        oldestTime = entry.lastUsed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      const entry = activeBlobUrls.get(oldestKey);
      if (entry?.blobUrl) {
        try {
          URL.revokeObjectURL(entry.blobUrl);
        } catch (_) {}
      }
      activeBlobUrls.delete(oldestKey);
    } else {
      break;
    }
  }
}

/**
 * Synchronously checks if a media URL is currently alive in the in-memory blob URL pool.
 * Returns the `blob:...` URL if available, or null.
 *
 * @param {string} rawUrl
 * @returns {string|null}
 */
export function getCachedBlobUrlSync(rawUrl) {
  if (!rawUrl) return null;
  if (rawUrl.startsWith('blob:') || rawUrl.startsWith('data:')) return rawUrl;

  const key = normalizeMediaKey(rawUrl);
  if (!key) return null;

  const entry = activeBlobUrls.get(key);
  if (entry?.blobUrl) {
    entry.lastUsed = Date.now();
    return entry.blobUrl;
  }

  return null;
}

/**
 * Retrieves a media Blob from on-device storage (IndexedDB or memory).
 * Automatically handles ArrayBuffer fallback decoding and registers a blob: URL in the memory pool.
 *
 * @param {string} rawUrl
 * @returns {Promise<{ blob: Blob, blobUrl: string, mimeType: string }|null>}
 */
export async function getCachedMedia(rawUrl) {
  if (!isCacheableUrl(rawUrl)) return null;

  const key = normalizeMediaKey(rawUrl);
  if (!key) return null;

  // 1. Check in-memory pool
  const memEntry = activeBlobUrls.get(key);
  if (memEntry?.blob && memEntry?.blobUrl) {
    memEntry.lastUsed = Date.now();
    return { blob: memEntry.blob, blobUrl: memEntry.blobUrl, mimeType: memEntry.mimeType };
  }

  // 2. Check IndexedDB persistent device storage
  try {
    const db = await openDb();
    const result = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();

    if (!result) return null;

    let blob = result.blob;
    // Reconstruct Blob if stored as ArrayBuffer for fallback compatibility
    if (!blob && result.buffer) {
      blob = new Blob([result.buffer], { type: result.mimeType || 'application/octet-stream' });
    }

    if (!blob) return null;

    const blobUrl = URL.createObjectURL(blob);
    const entry = {
      blobUrl,
      blob,
      mimeType: result.mimeType || blob.type || '',
      size: result.size || blob.size || 0,
      lastUsed: Date.now(),
    };

    activeBlobUrls.set(key, entry);
    evictOldestMemoryUrls();

    // Asynchronously bump lastAccessedAt in IndexedDB for LRU
    touchLastAccessed(key);

    return { blob, blobUrl, mimeType: entry.mimeType };
  } catch (err) {
    console.debug('[MediaCache] Read failed:', err?.message);
    return null;
  }
}

/**
 * Updates the lastAccessedAt timestamp for a stored entry in the background.
 * @param {string} key
 */
async function touchLastAccessed(key) {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => {
      const data = req.result;
      if (data) {
        data.lastAccessedAt = Date.now();
        store.put(data);
      }
    };
    tx.oncomplete = () => db.close();
  } catch (_) {}
}

/**
 * Stores a binary Blob into on-device IndexedDB storage and registers in-memory blob URL.
 *
 * @param {string} rawUrl
 * @param {Blob} blob
 * @param {string} [mimeType]
 * @returns {Promise<string>} The local blob: URL
 */
export async function storeMediaInCache(rawUrl, blob, mimeType = '') {
  if (!isCacheableUrl(rawUrl) || !blob) return '';

  const key = normalizeMediaKey(rawUrl);
  if (!key) return '';

  const effectiveMime = mimeType || blob.type || 'application/octet-stream';
  const size = blob.size || 0;
  const now = Date.now();

  // Create or reuse object URL
  let blobUrl = activeBlobUrls.get(key)?.blobUrl;
  if (!blobUrl) {
    blobUrl = URL.createObjectURL(blob);
  }

  activeBlobUrls.set(key, {
    blobUrl,
    blob,
    mimeType: effectiveMime,
    size,
    lastUsed: now,
  });
  evictOldestMemoryUrls();

  // Store in IndexedDB
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      const record = {
        key,
        blob,
        mimeType: effectiveMime,
        size,
        createdAt: now,
        lastAccessedAt: now,
      };

      try {
        store.put(record);
      } catch (idbErr) {
        // Fallback: If browser engine rejects Blob in IndexedDB, store as ArrayBuffer
        blob.arrayBuffer().then((buffer) => {
          try {
            const bufferRecord = {
              key,
              buffer,
              mimeType: effectiveMime,
              size,
              createdAt: now,
              lastAccessedAt: now,
            };
            store.put(bufferRecord);
          } catch (_) {}
        });
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();

    // Trigger lazy prune in idle time
    scheduleLazyPrune();
  } catch (err) {
    console.debug('[MediaCache] Save failed:', err?.message);
  }

  return blobUrl;
}

/**
 * Downloads a media resource from network and stores it on device.
 * Deduplicates concurrent requests for the exact same file.
 *
 * @param {string} rawUrl
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {function(number): void} [options.onProgress]
 * @returns {Promise<{ blob: Blob, blobUrl: string, mimeType: string }>}
 */
export async function fetchAndCacheMedia(rawUrl, { signal, onProgress } = {}) {
  if (!isCacheableUrl(rawUrl)) {
    return { blob: null, blobUrl: rawUrl, mimeType: '' };
  }

  const key = normalizeMediaKey(rawUrl);

  // 1. Check if already cached on device
  const cached = await getCachedMedia(rawUrl);
  if (cached) {
    if (typeof onProgress === 'function') onProgress(1);
    return cached;
  }

  // 2. Check if already being downloaded in-flight
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const downloadPromise = (async () => {
    try {
      const fullUrl = getMediaUrl(rawUrl);
      const response = await fetch(fullUrl, {
        signal,
        credentials: 'omit', // Public media; omit credentials prevents CORS issues with Wildcard
      });

      if (!response.ok) {
        throw new Error(`Media fetch failed with HTTP ${response.status}`);
      }

      const mimeType = response.headers.get('content-type') || '';
      const contentLength = Number(response.headers.get('content-length')) || 0;

      let blob;
      const reader = response.body && response.body.getReader ? response.body.getReader() : null;

      if (!reader || !contentLength) {
        blob = await response.blob();
        if (typeof onProgress === 'function') onProgress(1);
      } else {
        const chunks = [];
        let received = 0;
        let lastPaint = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.byteLength;

          const now = Date.now();
          if (now - lastPaint > 80 || received >= contentLength) {
            lastPaint = now;
            if (typeof onProgress === 'function') {
              onProgress(Math.min(1, received / contentLength));
            }
          }
        }

        blob = new Blob(chunks, { type: mimeType || 'application/octet-stream' });
        if (typeof onProgress === 'function') onProgress(1);
      }

      const blobUrl = await storeMediaInCache(rawUrl, blob, mimeType);
      return { blob, blobUrl, mimeType };
    } finally {
      inFlightRequests.delete(key);
    }
  })();

  inFlightRequests.set(key, downloadPromise);
  return downloadPromise;
}

/**
 * Preloads an array of media URLs in the background without blocking the UI thread.
 * Queues items and processes with controlled concurrency.
 *
 * @param {Array<string|object>} items - Array of URLs or message objects with mediaUrl
 */
export function preloadMessageMedia(items) {
  if (!Array.isArray(items) || items.length === 0) return;

  const urlsToQueue = items
    .map((item) => (typeof item === 'string' ? item : item?.mediaUrl))
    .filter((url) => isCacheableUrl(url));

  for (const url of urlsToQueue) {
    const key = normalizeMediaKey(url);
    if (!key || activeBlobUrls.has(key)) continue;
    if (!preloadQueue.includes(url)) {
      preloadQueue.push(url);
    }
  }

  processPreloadQueue();
}

function preloadSingleMediaItem(url) {
  activePreloadCount++;
  fetchAndCacheMedia(url)
    .catch(() => {})
    .finally(() => {
      activePreloadCount--;
      processPreloadQueue();
    });
}

function processPreloadQueue() {
  if (isPreloading || preloadQueue.length === 0) return;
  if (activePreloadCount >= MAX_CONCURRENT_PRELOAD) return;

  const run = () => {
    while (preloadQueue.length > 0 && activePreloadCount < MAX_CONCURRENT_PRELOAD) {
      const url = preloadQueue.shift();
      if (!url) continue;

      const key = normalizeMediaKey(url);
      if (activeBlobUrls.has(key)) continue;

      preloadSingleMediaItem(url);
    }
  };

  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 100);
  }
}

/**
 * Saves a media item directly to the user's device (downloads folder or photo gallery).
 * First attempts to use locally cached blob; falls back to network download if needed.
 *
 * @param {string} rawUrl
 * @param {string} [suggestedFilename]
 * @returns {Promise<boolean>} True on success
 */
export async function saveMediaToDevice(rawUrl, suggestedFilename = '') {
  try {
    let blob = null;
    let mime = '';

    // Check cache first
    const cached = await getCachedMedia(rawUrl);
    if (cached?.blob) {
      blob = cached.blob;
      mime = cached.mimeType || blob.type;
    } else {
      const downloaded = await fetchAndCacheMedia(rawUrl);
      blob = downloaded.blob;
      mime = downloaded.mimeType || blob?.type;
    }

    if (!blob) throw new Error('Could not obtain media blob');

    const filename =
      suggestedFilename ||
      rawUrl.split('/').pop().split('?')[0] ||
      `bazzef_media_${Date.now()}.${mime.includes('video') ? 'mp4' : mime.includes('audio') ? 'mp3' : 'jpg'}`;

    // Standard download anchor (prompts browser/device download or saves to gallery)
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();

    setTimeout(() => {
      try {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(downloadUrl);
      } catch (_) {}
    }, 1200);

    return true;
  } catch (err) {
    console.error('[MediaCache] Save to device failed:', err);
    // Ultimate fallback: open in new tab
    try {
      window.open(getMediaUrl(rawUrl), '_blank');
      return true;
    } catch (_) {
      return false;
    }
  }
}

/**
 * Lazy LRU pruning: keeps IndexedDB usage within bounds.
 */
let pruneScheduled = false;
function scheduleLazyPrune() {
  if (pruneScheduled) return;
  pruneScheduled = true;

  const run = async () => {
    pruneScheduled = false;
    try {
      const db = await openDb();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('by_accessed');

      const allRecords = await new Promise((resolve) => {
        const req = index.getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => resolve([]);
      });

      let totalBytes = 0;
      for (const rec of allRecords) {
        totalBytes += rec.size || 0;
      }

      if (allRecords.length > MAX_CACHE_ENTRIES || totalBytes > MAX_CACHE_BYTES) {
        // Records from index are sorted ascending by lastAccessedAt (oldest first)
        const entriesToDelete = [];
        let bytesToRemove = Math.max(0, totalBytes - (MAX_CACHE_BYTES * 0.8));
        let countToRemove = Math.max(0, allRecords.length - Math.floor(MAX_CACHE_ENTRIES * 0.8));

        for (const rec of allRecords) {
          if (countToRemove > 0 || bytesToRemove > 0) {
            entriesToDelete.push(rec.key);
            countToRemove--;
            bytesToRemove -= rec.size || 0;
          } else {
            break;
          }
        }

        for (const k of entriesToDelete) {
          store.delete(k);
          activeBlobUrls.delete(k);
        }
      }

      tx.oncomplete = () => db.close();
    } catch (_) {}
  };

  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(run, { timeout: 5000 });
  } else {
    setTimeout(run, 1500);
  }
}

/**
 * Completely clears all cached media from device storage and revokes object URLs.
 */
export async function clearMediaCache() {
  for (const entry of activeBlobUrls.values()) {
    if (entry.blobUrl) {
      try {
        URL.revokeObjectURL(entry.blobUrl);
      } catch (_) {}
    }
  }
  activeBlobUrls.clear();

  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => db.close();
  } catch (_) {}
}

/**
 * Returns summary storage statistics of media stored on user device.
 * @returns {Promise<{ count: number, totalBytes: number, formattedSize: string }>}
 */
export async function getMediaCacheStats() {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    const records = await new Promise((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
    db.close();

    const count = records.length;
    let totalBytes = 0;
    for (const r of records) {
      totalBytes += r.size || 0;
    }

    const mb = (totalBytes / (1024 * 1024)).toFixed(1);
    return { count, totalBytes, formattedSize: `${mb} MB` };
  } catch {
    return { count: 0, totalBytes: 0, formattedSize: '0 MB' };
  }
}
